// src/pages/settings/ComprehensiveUserManagement.tsx
// Migrated to Supabase with advanced user management features

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import { userService } from '../../services/userService';
import { adminUsersService } from '../../services/adminUsersService';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import { displayLearningArea } from '../../constants/learningAreas';
import { staffCodeCandidates, pickStaffCode } from '../../lib/staffCode';
import {
  Users,
  Search,
  Filter,
  Plus,
  Edit3,
  Trash2,
  UserCheck,
  UserX,
  Shield,
  Mail,
  MoreVertical,
  Download,
  Upload,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  UserCog
} from 'lucide-react';

interface User {
  id: string;
  email: string;
  staff_id?: string;
  name?: string;
  role: 'admin' | 'system_admin' | 'principal' | 'assistant_principal' | 'head_of_department' | 'curriculum_leader' | 'teacher';
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  curriculum_area?: string;
  department?: string;
  employment_type: 'full_time' | 'part_time' | 'casual' | 'contract';
  fte: number;
  permissions: string[];
  created_at: string;
  updated_at: string;
  last_sign_in_at?: string;
  email_confirmed_at?: string;
}

interface UserStats {
  total: number;
  active: number;
  inactive: number;
  pending: number;
  suspended: number;
  admins: number;
  teachers: number;
}

const ComprehensiveUserManagement: React.FC = () => {
  const { user: currentUser, impersonateUser } = useAuth();
  const { refreshStaff } = useData();
  const confirm = useConfirm();
  const { addToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  // Staff code is edited explicitly rather than on-change: it re-keys the
  // person's grid allocations, so it needs a deliberate Save (#206).
  const [staffCodeDraft, setStaffCodeDraft] = useState('');
  const [savingStaffCode, setSavingStaffCode] = useState(false);
  const [staffCodeError, setStaffCodeError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<UserStats>({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0,
    suspended: 0,
    admins: 0,
    teachers: 0
  });

  const roles = [
    { value: 'admin', label: 'Admin' },
    { value: 'system_admin', label: 'System Admin' },
    { value: 'principal', label: 'Principal' },
    { value: 'assistant_principal', label: 'Assistant Principal' },
    { value: 'head_of_department', label: 'Head of Department' },
    { value: 'curriculum_leader', label: 'Curriculum Leader' },
    { value: 'teacher', label: 'Teacher' }
  ];

  const statuses = [
    { value: 'active', label: 'Active', color: 'green' },
    { value: 'inactive', label: 'Inactive', color: 'gray' },
    { value: 'pending', label: 'Pending', color: 'yellow' },
    { value: 'suspended', label: 'Suspended', color: 'red' }
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, selectedRole, selectedStatus]);

  /**
   * Call after ANY write to `staff` from this page.
   *
   * This screen writes to `staff` directly rather than through DataContext, so
   * reloading only its own list leaves every other consumer of the shared roster
   * stale — the staff directory, allowances, and the intentions "fill on behalf"
   * picker — until the admin does a full page reload. That is issue #203: a newly
   * added user did not show up as someone an admin could submit an intention for.
   *
   * There is no realtime fallback; see the warning in DataContext.
   */
  const reloadAfterStaffWrite = async () => {
    await Promise.all([loadUsers(), refreshStaff()]);
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Get all staff data - this is now our single source of truth
      const { data: staffData, error: staffError } = await supabase
        .from('staff')
        .select('*');

      if (staffError) throw staffError;

      // Convert staff data to User format
      const users: User[] = (staffData || []).map(staff => ({
        id: staff.id,
        email: staff.email,
        staff_id: staff.staff_code,
        name: staff.name,
        role: staff.role || 'teacher',
        status: staff.status || 'pending',
        curriculum_area: staff.curriculum_area,
        department: staff.department,
        employment_type: staff.employment_type || 'full_time',
        fte: staff.fte || 1.0,
        permissions: staff.permissions || [],
        created_at: staff.created_at || new Date().toISOString(),
        updated_at: staff.updated_at || new Date().toISOString(),
        // For auth data, we'll check if this staff member has an auth account
        last_sign_in_at: undefined, // Would need to get from auth.users if needed
        email_confirmed_at: undefined // Would need to get from auth.users if needed
      }));

      setUsers(users);
      calculateStats(users);
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (userList: User[]) => {
    const newStats: UserStats = {
      total: userList.length,
      active: userList.filter(u => u.status === 'active').length,
      inactive: userList.filter(u => u.status === 'inactive').length,
      pending: userList.filter(u => u.status === 'pending').length,
      suspended: userList.filter(u => u.status === 'suspended').length,
      admins: userList.filter(u => ['admin', 'system_admin', 'principal'].includes(u.role)).length,
      teachers: userList.filter(u => u.role === 'teacher').length
    };
    setStats(newStats);
  };

  const filterUsers = () => {
    let filtered = users;

    if (searchTerm) {
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(user =>
        (user.email?.toLowerCase() || '').includes(normalizedSearchTerm) ||
        (user.name?.toLowerCase() || '').includes(normalizedSearchTerm) ||
        (user.staff_id?.toLowerCase() || '').includes(normalizedSearchTerm)
      );
    }

    if (selectedRole !== 'all') {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(user => user.status === selectedStatus);
    }

    setFilteredUsers(filtered);
  };

  /**
   * Change a staff member's code.
   *
   * This is not a cosmetic rename. Grid allocations, load letters and teaching
   * intentions all reference a person by CODE, not by id — `lineAssignments` is
   * `{ "A": "MJN" }` — so changing it detaches every one of those from this
   * person until they are re-pointed. The modal says so before you save.
   *
   * Uniqueness is enforced by uq_pit_staff_tenant_staff_code; a clash comes back
   * as 23505 and is translated rather than shown raw (#206).
   */
  const updateStaffCode = async (userId: string, rawCode: string) => {
    const code = rawCode.trim().toUpperCase();
    setStaffCodeError(null);

    if (!code) {
      setStaffCodeError('Staff code cannot be empty.');
      return;
    }
    if (!/^[A-Z0-9-]{1,10}$/.test(code)) {
      setStaffCodeError('Use letters, numbers or hyphens only (max 10 characters).');
      return;
    }
    if (code === (selectedUser?.staff_id || '').toUpperCase()) {
      setStaffCodeError('That is already the code for this person.');
      return;
    }

    setSavingStaffCode(true);
    try {
      const { error: staffError } = await supabase
        .from('staff')
        .update({ staff_code: code, staff_id: code, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (staffError) {
        if (staffError.code === '23505') {
          setStaffCodeError(`Staff code "${code}" is already used by someone else.`);
          return;
        }
        throw staffError;
      }

      await reloadAfterStaffWrite();

      if (selectedUser?.id === userId) {
        setSelectedUser({ ...selectedUser, staff_id: code, updated_at: new Date().toISOString() });
      }
      setStaffCodeDraft(code);
      setError(null);
    } catch (err) {
      console.error('Error updating staff code:', err);
      setStaffCodeError(err instanceof Error ? err.message : 'Could not update the staff code.');
    } finally {
      setSavingStaffCode(false);
    }
  };

  const updateUserStatus = async (userId: string, newStatus: string) => {
    try {
      // Update the staff table - our single source of truth
      const { error: staffError } = await supabase
        .from('staff')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (staffError) throw staffError;

      await reloadAfterStaffWrite();

      // Update selectedUser to reflect the new status in the modal
      if (selectedUser?.id === userId) {
        setSelectedUser({
          ...selectedUser,
          status: newStatus as any,
          updated_at: new Date().toISOString()
        });
      }

      setError(null);
    } catch (err) {
      console.error('Error updating user status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user status');
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      // Update the staff table - our single source of truth
      const { error: staffError } = await supabase
        .from('staff')
        .update({
          role: newRole,
          permissions: getUserPermissions(newRole),
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (staffError) throw staffError;

      await reloadAfterStaffWrite();

      // Update selectedUser to reflect the new role in the modal
      if (selectedUser?.id === userId) {
        setSelectedUser({
          ...selectedUser,
          role: newRole as any,
          permissions: getUserPermissions(newRole),
          updated_at: new Date().toISOString()
        });
      }

      setError(null);
    } catch (err) {
      console.error('Error updating user role:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user role');
    }
  };

  const deleteUser = async (userId: string) => {
    if (!(await confirm({
      title: 'Delete user?',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    }))) {
      return;
    }

    try {
      // Delete from auth — server-side via Edge Function. The previous
      // client-side supabase.auth.admin.deleteUser silently failed in
      // production because the browser uses the anon key (issue #17).
      await adminUsersService.delete(userId);

      // Delete from staff table (CASCADE should handle this)
      const { error: staffError } = await supabase
        .from('staff')
        .delete()
        .eq('id', userId);

      if (staffError) throw staffError;

      await reloadAfterStaffWrite();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleImpersonate = async (user: User) => {
    if (!(await confirm({
      title: `Impersonate ${user.name || user.email}?`,
      message: `You will see exactly what this user sees, including their navigation, dashboard, and permissions.`,
      confirmLabel: 'Impersonate',
      variant: 'primary',
    }))) {
      return;
    }

    try {
      await impersonateUser(user.email);
    } catch (err) {
      console.error('Error impersonating user:', err);
      setError(err instanceof Error ? err.message : 'Failed to impersonate user');
    }
  };

  const addUser = async (userData: {
    email: string;
    name: string;
    role: string;
    department?: string;
    curriculum_area?: string;
    employment_type: string;
    fte: number;
  }) => {
    try {
      // Normalize email to lowercase for consistent matching
      const normalizedEmail = userData.email.toLowerCase().trim();

      // Guard against duplicates before inserting. The staff table has a
      // UNIQUE (email) constraint, so adding someone who already exists
      // would otherwise fail with a cryptic Postgres error (code 23505).
      const { data: existing, error: existingError } = await supabase
        .from('staff')
        .select('name, email')
        .eq('email', normalizedEmail)
        .maybeSingle();

      if (existingError) throw existingError;

      if (existing) {
        setError(
          `A staff member with the email ${normalizedEmail} already exists` +
          (existing.name ? ` (${existing.name})` : '') +
          `. They're already in the system — search for them in the list instead of adding them again.`
        );
        return;
      }

      // Create staff record - this is now our single source of truth
      const userId = crypto.randomUUID();

      // Assign a real staff_code now (#204). Without one this person is invisible
      // to grids, load letters, preference matching and the own-row RLS policies
      // on teaching_intentions — and any intention filed for them gets an
      // invented, unstable code that attaches to nobody.
      const takenCodes = (await supabase.from('staff').select('staff_code')).data
        ?.map(row => row.staff_code)
        .filter(Boolean) as string[] | undefined;

      const candidates = staffCodeCandidates(userData.name);
      if (candidates.length === 0) {
        setError(
          `Could not derive a staff code from the name "${userData.name}". ` +
          `Please use a name containing letters.`
        );
        return;
      }

      const preferred = pickStaffCode(userData.name, takenCodes ?? []);
      // Try the best candidate first, then walk the rest. The unique index
      // uq_pit_staff_tenant_staff_code is the real arbiter, so a code taken by a
      // concurrent admin between our read and our write just costs one retry.
      const ordered = preferred
        ? [preferred, ...candidates.filter(c => c !== preferred)]
        : candidates;

      let staffError: { code?: string; message?: string; details?: string } | null = null;
      let assignedCode: string | null = null;

      for (const candidate of ordered) {
        const { error } = await supabase
          .from('staff')
          .insert({
            id: userId,
            email: normalizedEmail, // Use normalized email
            name: userData.name,
            role: userData.role,
            department: userData.department,
            curriculum_area: userData.curriculum_area,
            staff_code: candidate,
            status: 'active', // Set to active so they can login immediately with SSO
            employment_type: userData.employment_type,
            fte: userData.fte,
            permissions: getUserPermissions(userData.role),
            school_id: 'scr-vic-edu-au'
          });

        if (!error) {
          assignedCode = candidate;
          staffError = null;
          break;
        }

        staffError = error;
        // Only a staff_code clash is worth retrying. An email clash means the
        // person already exists, and is handled by the catch below.
        const clashedOnCode =
          error.code === '23505' && `${error.message} ${error.details ?? ''}`.includes('staff_code');
        if (!clashedOnCode) break;
      }

      if (staffError) throw staffError;
      if (!assignedCode) {
        setError(
          `Could not find a free staff code for ${userData.name}. ` +
          `Please set one manually or contact support.`
        );
        return;
      }

      setError(null);
      setShowAddModal(false);
      await reloadAfterStaffWrite();

      // Name the assigned staff code explicitly. It is derived from the name, not
      // read from the college's authoritative StaffCodes list, so the admin needs
      // to see it to notice a mismatch. There is no UI to correct it yet (#206).
      addToast(
        `User ${userData.name} has been added with staff code ${assignedCode}. ` +
        `They can now sign in using their email address (${normalizedEmail}) via SSO. ` +
        `If ${assignedCode} isn't their usual college code, let an administrator know.`,
        'success'
      );
    } catch (err) {
      console.error('Error adding user:', err);
      // Safety net: a unique-violation can still occur if two admins add
      // the same person at the same time (race past the pre-check above).
      const code = (err as { code?: string })?.code;
      if (code === '23505') {
        setError(
          `A staff member with the email ${userData.email.toLowerCase().trim()} already exists. ` +
          `They're already in the system — search for them in the list instead of adding them again.`
        );
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to add user');
    }
  };

  // Helper function to get permissions based on role
  const getUserPermissions = (role: string): string[] => {
    switch (role) {
      case 'admin':
      case 'system_admin':
        return ['admin', 'user_management', 'system_config', 'view_all', 'manage_grids', 'view_reports'];
      case 'principal':
      case 'assistant_principal':
        return ['view_all', 'manage_grids', 'view_reports', 'user_management'];
      case 'head_of_department':
      case 'curriculum_leader':
        return ['view_all', 'manage_grids', 'view_reports'];
      case 'teacher':
      default:
        return ['view_own', 'edit_preferences'];
    }
  };

  const bulkUpdateStatus = async (newStatus: string) => {
    if (selectedUsers.size === 0) return;

    try {
      const userIds = Array.from(selectedUsers);
      const { error } = await supabase
        .from('staff')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .in('id', userIds);

      if (error) throw error;

      await reloadAfterStaffWrite();
      setSelectedUsers(new Set());
    } catch (err) {
      console.error('Error bulk updating users:', err);
      setError(err instanceof Error ? err.message : 'Failed to update users');
    }
  };

  const exportUsers = () => {
    const csvContent = [
      'Email,Name,Staff ID,Role,Status,Learning Area,FTE,Last Sign In',
      ...filteredUsers.map(user => 
        `${user.email},"${user.name || ''}","${user.staff_id || ''}",${user.role},${user.status},"${user.department || ''}",${user.fte},"${user.last_sign_in_at || ''}"`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4 text-ok-fg" />;
      case 'inactive': return <XCircle className="h-4 w-4 text-ink-500" />;
      case 'pending': return <Clock className="h-4 w-4 text-warn-fg" />;
      case 'suspended': return <AlertTriangle className="h-4 w-4 text-danger-fg" />;
      default: return <Clock className="h-4 w-4 text-ink-500" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'system_admin': return 'bg-navy-100 text-navy-800';
      case 'admin': return 'bg-danger-bg text-danger-fg';
      case 'principal': return 'bg-navy-100 text-navy-800';
      case 'assistant_principal': return 'bg-navy-100 text-navy-800';
      case 'head_of_department': return 'bg-ok-bg text-ok-fg';
      case 'curriculum_leader': return 'bg-warn-bg text-warn-fg';
      default: return 'bg-ink-100 text-ink-900';
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Comprehensive User Management"
          description="Advanced user and role management system"
        />
        <Card>
          <div className="p-8 flex justify-center">
            <Spinner size="lg" label="Loading users…" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Comprehensive User Management"
        description="Advanced user and role management system"
      />

      {error && (
        <Card>
          <div className="p-4 bg-danger-bg border border-danger-border rounded-md">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-danger-solid" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-danger-fg">Error</h3>
                <p className="mt-1 text-sm text-danger-fg">{error}</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-navy-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-ink-500">Total Users</p>
                <p className="font-mono tabular-nums text-2xl font-bold text-ink-900">{stats.total}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <UserCheck className="h-8 w-8 text-ok-fg" />
              <div className="ml-4">
                <p className="text-sm font-medium text-ink-500">Active Users</p>
                <p className="font-mono tabular-nums text-2xl font-bold text-ink-900">{stats.active}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-navy-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-ink-500">Administrators</p>
                <p className="font-mono tabular-nums text-2xl font-bold text-ink-900">{stats.admins}</p>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-warn-fg" />
              <div className="ml-4">
                <p className="text-sm font-medium text-ink-500">Pending</p>
                <p className="font-mono tabular-nums text-2xl font-bold text-ink-900">{stats.pending}</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Controls */}
      <Card>
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
                />
              </div>

              {/* Role Filter */}
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
              >
                <option value="all">All Roles</option>
                {roles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
              >
                <option value="all">All Statuses</option>
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center px-4 py-2 bg-navy-600 text-white rounded-md hover:bg-navy-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </button>
              <button
                onClick={exportUsers}
                className="flex items-center px-4 py-2 bg-ink-500 text-white rounded-md hover:bg-ink-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedUsers.size > 0 && (
            <div className="mt-4 p-4 bg-navy-50 border border-navy-200 rounded-md">
              <div className="flex items-center justify-between">
                <span className="text-sm text-navy-700">
                  {selectedUsers.size} user(s) selected
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => bulkUpdateStatus('active')}
                    className="px-3 py-1 bg-ok-solid text-white text-sm rounded hover:bg-ok-solid"
                  >
                    Activate
                  </button>
                  <button
                    onClick={() => bulkUpdateStatus('inactive')}
                    className="px-3 py-1 bg-ink-500 text-white text-sm rounded hover:bg-ink-700"
                  >
                    Deactivate
                  </button>
                  <button
                    onClick={() => bulkUpdateStatus('suspended')}
                    className="px-3 py-1 bg-danger-solid text-white text-sm rounded hover:bg-danger-solid"
                  >
                    Suspend
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ink-200">
            <thead className="bg-ink-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
                      } else {
                        setSelectedUsers(new Set());
                      }
                    }}
                    checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Learning Area
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Last Sign In
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-ink-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-ink-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedUsers);
                        if (e.target.checked) {
                          newSelected.add(user.id);
                        } else {
                          newSelected.delete(user.id);
                        }
                        setSelectedUsers(newSelected);
                      }}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-ink-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-ink-700">
                            {user.name?.charAt(0)?.toUpperCase() || user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-ink-900">
                          {user.name || 'No name'}
                        </div>
                        <div className="text-sm text-ink-500">{user.email}</div>
                        {user.staff_id && (
                          <div className="font-mono tabular-nums text-xs text-ink-400">ID: {user.staff_id}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                      {roles.find(r => r.value === user.role)?.label || user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(user.status)}
                      <span className="ml-2 text-sm text-ink-900 capitalize">{user.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">
                    {displayLearningArea(user.department, '-')}
                  </td>
                  <td className="font-mono tabular-nums px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                    {user.last_sign_in_at
                      ? new Date(user.last_sign_in_at).toLocaleDateString()
                      : 'Never'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setStaffCodeDraft(user.staff_id || '');
                          setStaffCodeError(null);
                          setShowUserModal(true);
                        }}
                        className="text-navy-600 hover:text-navy-900"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setStaffCodeDraft(user.staff_id || '');
                          setStaffCodeError(null);
                          setShowUserModal(true);
                        }}
                        className="text-navy-600 hover:text-navy-900"
                        title="Edit User"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleImpersonate(user)}
                        className="text-navy-600 hover:text-navy-900 disabled:text-ink-400 disabled:cursor-not-allowed"
                        title="Impersonate User"
                        disabled={user.email === currentUser?.email}
                      >
                        <UserCog className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteUser(user.id)}
                        className="text-danger-fg hover:text-danger-fg"
                        title="Delete User"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && !loading && (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title="No users found"
            description={
              searchTerm || selectedRole !== 'all' || selectedStatus !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Get started by adding your first user.'
            }
          />
        )}
      </Card>

      {/* User Detail/Edit Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-card-title text-ink-900 mb-4">
                User Details: {selectedUser.name || selectedUser.email}
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Email</label>
                    <p className="mt-1 text-sm text-ink-900">{selectedUser.email}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-ink-700">Staff code</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={staffCodeDraft}
                        onChange={(e) => { setStaffCodeDraft(e.target.value.toUpperCase()); setStaffCodeError(null); }}
                        placeholder="Not set"
                        maxLength={10}
                        className="block w-40 px-3 py-2 border border-ink-300 rounded-md shadow-sm font-mono tabular-nums uppercase focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                      />
                      <button
                        onClick={() => updateStaffCode(selectedUser.id, staffCodeDraft)}
                        disabled={savingStaffCode || !staffCodeDraft.trim()
                          || staffCodeDraft.trim().toUpperCase() === (selectedUser.staff_id || '').toUpperCase()}
                        className="px-3 py-2 rounded-md text-sm bg-navy-600 text-white hover:bg-navy-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {savingStaffCode ? 'Saving…' : 'Save code'}
                      </button>
                    </div>
                    {staffCodeError && (
                      <p className="mt-1 text-sm text-danger-fg">{staffCodeError}</p>
                    )}
                    {/* Grid allocations, load letters and intentions all key off the
                        CODE, not the row id. Changing it silently detaches them, so
                        the warning has to be explicit rather than a tooltip (#206). */}
                    <p className="mt-1 text-xs text-ink-500">
                      Grid allocations, load letters and teaching intentions all refer to a person by
                      this code. Changing it detaches this person from everything already recorded
                      against the old code — those will need re-pointing by hand.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Role</label>
                    <select
                      value={selectedUser.role}
                      onChange={(e) => updateUserRole(selectedUser.id, e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                    >
                      {roles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Status</label>
                    <select
                      value={selectedUser.status}
                      onChange={(e) => updateUserStatus(selectedUser.id, e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                    >
                      {statuses.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Learning Area</label>
                    <p className="mt-1 text-sm text-ink-900">{displayLearningArea(selectedUser.department, 'Not set')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">FTE</label>
                    <p className="mt-1 font-mono tabular-nums text-sm text-ink-900">{selectedUser.fte}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Created</label>
                    <p className="mt-1 font-mono tabular-nums text-sm text-ink-900">
                      {new Date(selectedUser.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Last Sign In</label>
                    <p className="mt-1 font-mono tabular-nums text-sm text-ink-900">
                      {selectedUser.last_sign_in_at
                        ? new Date(selectedUser.last_sign_in_at).toLocaleDateString()
                        : 'Never'
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowUserModal(false)}
                  className="px-4 py-2 border border-ink-300 rounded-md text-ink-700 hover:bg-ink-50"
                >
                  Close
                </button>
                <button
                  onClick={() => deleteUser(selectedUser.id)}
                  className="px-4 py-2 bg-danger-solid text-white rounded-md hover:bg-danger-solid"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-card-title text-ink-900 mb-4">Add New User</h3>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                addUser({
                  email: formData.get('email') as string,
                  name: formData.get('name') as string,
                  role: formData.get('role') as string,
                  department: formData.get('department') as string || undefined,
                  curriculum_area: formData.get('curriculum_area') as string || undefined,
                  employment_type: formData.get('employment_type') as string,
                  fte: parseFloat(formData.get('fte') as string)
                });
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Role</label>
                    <select
                      name="role"
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                    >
                      {roles.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Learning Area</label>
                    <input
                      type="text"
                      name="department"
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Learning Area (leads)</label>
                    <input
                      type="text"
                      name="curriculum_area"
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">Employment Type</label>
                    <select
                      name="employment_type"
                      defaultValue="full_time"
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                    >
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="casual">Casual</option>
                      <option value="contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700">FTE (Full Time Equivalent)</label>
                    <input
                      type="number"
                      name="fte"
                      min="0"
                      max="1"
                      step="0.1"
                      defaultValue="1.0"
                      className="mt-1 block w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:outline-none focus:ring-navy-500 focus:border-navy-500"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-ink-300 rounded-md text-ink-700 hover:bg-ink-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-navy-600 text-white rounded-md hover:bg-navy-700"
                  >
                    Add User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComprehensiveUserManagement;